import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Paper, Divider, Card, CardContent, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, MenuItem, Select, CircularProgress, Alert } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, People as PeopleIcon, Payments as PaymentsIcon, Savings as SavingsIcon, CreditCard as CreditCardIcon, AccountBalance as AccountBalanceIcon, Apartment as ApartmentIcon, Agriculture as AgricultureIcon, EmojiEvents as EmojiEventsIcon, LocalAtm as LocalAtmIcon, MonetizationOn as MonetizationOnIcon } from '@mui/icons-material';
import { collection, query, orderBy, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import StatisticsManager from './StatisticsManager';

// Composant pour afficher une prévisualisation de la statistique
const StatPreview = ({ stat }) => {
  // Fonction pour obtenir l'icône correspondante
  const getIcon = (iconName) => {
    const icons = {
      'People': <PeopleIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'Payments': <PaymentsIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'Savings': <SavingsIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'CreditCard': <CreditCardIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'AccountBalance': <AccountBalanceIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'Apartment': <ApartmentIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'Agriculture': <AgricultureIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'EmojiEvents': <EmojiEventsIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'LocalAtm': <LocalAtmIcon sx={{ fontSize: 50, color: '#014527' }} />,
      'MonetizationOn': <MonetizationOnIcon sx={{ fontSize: 50, color: '#014527' }} />
    };
    return icons[iconName] || <PeopleIcon sx={{ fontSize: 50, color: '#014527' }} />;
  };

  // S'assurer que la valeur est un nombre valide pour éviter les erreurs
  const safeValue = typeof stat.value === 'number' ? stat.value : 0;
  
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 4 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        borderRadius: 2,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)'
        },
        minWidth: 0,
        maxWidth: 350,
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: { xs: 1, sm: 2 } }}>
        {getIcon(stat.icon)}
      </Box>
      <Box sx={{ mb: 1, width: '100%' }}>
        <Typography
          variant="h3"
          component="span"
          sx={{
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            display: 'inline-block',
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            letterSpacing: 0.5,
            wordBreak: 'break-word',
          }}
        >
          {stat.prefix && `${stat.prefix} `}{safeValue.toLocaleString()}{stat.suffix && ` ${stat.suffix}`}
        </Typography>
      </Box>
      <Typography
        variant="h6"
        component="h3"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: '#014527',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          wordBreak: 'break-word',
        }}
      >
        {stat.label}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mt: 'auto', fontSize: { xs: '0.95rem', sm: '1rem' }, wordBreak: 'break-word' }}
      >
        {stat.description}
      </Typography>
    </Paper>
  );
};

const Statistics = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Effet pour gérer le timeout des messages de succès
  useEffect(() => {
    let successTimer;
    if (success) {
      successTimer = setTimeout(() => {
        setSuccess('');
      }, 3000);
    }
    return () => {
      if (successTimer) clearTimeout(successTimer);
    };
  }, [success]);
  
  // État pour le dialogue d'ajout/édition
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStat, setCurrentStat] = useState({
    id: '',
    icon: 'People',
    value: 0,
    prefix: '',
    suffix: '',
    label: '',
    description: '',
    order: 0
  });

  // Liste des icônes disponibles
  const availableIcons = [
    { name: 'People', label: 'Personnes' },
    { name: 'Payments', label: 'Paiements' },
    { name: 'Savings', label: 'Économies' },
    { name: 'CreditCard', label: 'Carte de crédit' },
    { name: 'AccountBalance', label: 'Banque' },
    { name: 'Apartment', label: 'Bâtiment' },
    { name: 'Agriculture', label: 'Agriculture' },
    { name: 'EmojiEvents', label: 'Événements' },
    { name: 'LocalAtm', label: 'Argent' },
    { name: 'MonetizationOn', label: 'Monétisation' }
  ];

  // Charger les statistiques depuis Firestore
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(''); // Réinitialiser les erreurs précédentes
        
        // Créer une référence valide à la collection
        const statsRef = collection(db, 'statistics');
        const statsQuery = query(statsRef, orderBy('order', 'asc'));
        const statsSnapshot = await getDocs(statsQuery);
        
        const statsData = statsSnapshot.docs.map(doc => {
          // S'assurer que chaque document a un ID valide
          if (!doc.id) {
            console.warn('Document sans ID détecté dans la collection statistics');
            return null;
          }
          
          return {
            id: doc.id,
            ...doc.data(),
            // S'assurer que value est un nombre
            value: typeof doc.data().value === 'number' ? doc.data().value : 0
          };
        }).filter(Boolean); // Filtrer les éléments null
        
        setStats(statsData);
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        setError(`Impossible de charger les statistiques: ${err.message}`);
        setStats([]); // Réinitialiser les stats en cas d'erreur
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);


  // Ouvrir le dialogue pour ajouter une nouvelle statistique
  const handleAddStat = () => {
    setCurrentStat({
      id: '',
      icon: 'People',
      value: 0,
      prefix: '',
      suffix: '',
      label: '',
      description: '',
      order: stats.length
    });
    setIsEditing(false);
    setOpen(true);
  };

  // Ouvrir le dialogue pour éditer une statistique existante
  const handleEditStat = (stat) => {
    setCurrentStat(stat);
    setIsEditing(true);
    setOpen(true);
  };

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convertir la valeur en nombre si c'est le champ 'value'
    if (name === 'value') {
      setCurrentStat({
        ...currentStat,
        [name]: parseFloat(value) || 0
      });
    } else {
      setCurrentStat({
        ...currentStat,
        [name]: value
      });
    }
  };

  // Sauvegarder la statistique (ajout ou mise à jour)
  const handleSave = async () => {
    try {
      // Réinitialiser les messages d'erreur et de succès
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Vérifier que les champs obligatoires sont remplis
      if (!currentStat.label) {
        setError('Veuillez saisir un titre pour la statistique.');
        setLoading(false);
        return;
      }
      
      if (currentStat.value === undefined || currentStat.value === null) {
        setError('Veuillez saisir une valeur numérique pour la statistique.');
        setLoading(false);
        return;
      }
      
      const statData = {
        ...currentStat,
        updatedAt: serverTimestamp()
      };
      
      // Si c'est une nouvelle statistique, générer un ID
      if (!isEditing) {
        const newStatId = `stat_${Date.now()}`;
        statData.createdAt = serverTimestamp();
        // Supprimer l'ID vide du statData pour éviter les erreurs
        delete statData.id;
        await setDoc(doc(db, 'statistics', newStatId), statData);
        setSuccess('Statistique ajoutée avec succès!');
      } else {
        // Mise à jour d'une statistique existante
        // Vérifier que l'ID est valide
        if (!currentStat.id || typeof currentStat.id !== 'string' || currentStat.id.trim() === '') {
          setError('ID de statistique invalide. Veuillez rafraîchir la page et réessayer.');
          setLoading(false);
          return;
        }
        
        // Créer une copie sans l'ID pour éviter qu'il soit stocké dans le document
        const dataToUpdate = { ...statData };
        delete dataToUpdate.id;
        
        await setDoc(doc(db, 'statistics', currentStat.id), dataToUpdate, { merge: true });
        setSuccess('Statistique mise à jour avec succès!');
      }
      
      // Recharger les statistiques
      const statsQuery = query(collection(db, 'statistics'), orderBy('order', 'asc'));
      const statsSnapshot = await getDocs(statsQuery);
      const statsData = statsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setStats(statsData);
      setOpen(false);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la statistique:', err);
      setSuccess(''); // Effacer tout message de succès en cas d'erreur
      setError(`Impossible de sauvegarder la statistique: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une statistique
  const handleDeleteStat = async (statId) => {
    // Réinitialiser les messages d'erreur et de succès
    setError('');
    setSuccess('');
    
    // Vérifier que l'ID est valide avant de tenter la suppression
    if (!statId || typeof statId !== 'string' || statId.trim() === '') {
      setError('ID de statistique invalide. Impossible de supprimer cette statistique.');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette statistique?')) {
      try {
        setLoading(true);
        
        // Créer une référence valide au document
        const statRef = doc(db, 'statistics', statId);
        await deleteDoc(statRef);
        
        // Mettre à jour la liste des statistiques
        setStats(stats.filter(stat => stat.id !== statId));
        setSuccess('Statistique supprimée avec succès!');
      } catch (err) {
        console.error('Erreur lors de la suppression de la statistique:', err);
        setSuccess(''); // Effacer tout message de succès en cas d'erreur
        setError(`Impossible de supprimer la statistique: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#014527', fontWeight: 'bold' }}>
          Gestion des statistiques
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddStat}
          sx={{ bgcolor: '#014527', '&:hover': { bgcolor: '#01331d' } }}
        >
          Ajouter une statistique
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      {loading && stats.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Aperçu des statistiques
          </Typography>
          
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {stats.map((stat) => (
              <Grid item xs={12} sm={6} md={4} key={stat.id}>
                <StatPreview stat={stat} />
              </Grid>
            ))}
          </Grid>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            Liste des statistiques
          </Typography>
          
          <Grid container spacing={2}>
            {stats.map((stat) => (
              <Grid item xs={12} key={stat.id}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 2 }}>
                          {(() => {
                            const IconComponent = {
                              'People': PeopleIcon,
                              'Payments': PaymentsIcon,
                              'Savings': SavingsIcon,
                              'CreditCard': CreditCardIcon,
                              'AccountBalance': AccountBalanceIcon,
                              'Apartment': ApartmentIcon,
                              'Agriculture': AgricultureIcon,
                              'EmojiEvents': EmojiEventsIcon,
                              'LocalAtm': LocalAtmIcon,
                              'MonetizationOn': MonetizationOnIcon
                            }[stat.icon] || PeopleIcon;
                            
                            return <IconComponent sx={{ color: '#014527' }} />;
                          })()}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {stat.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'block' }}>
                            {stat.prefix && `${stat.prefix} `}{(typeof stat.value === 'number' ? stat.value : 0).toLocaleString()}{stat.suffix} - {stat.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <IconButton onClick={() => handleEditStat(stat)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteStat(stat.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      
      {/* Dialogue d'ajout/édition de statistique */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Modifier la statistique' : 'Ajouter une nouvelle statistique'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Titre"
                name="label"
                value={currentStat.label}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={currentStat.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valeur"
                name="value"
                type="number"
                value={currentStat.value}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Préfixe"
                name="prefix"
                value={currentStat.prefix}
                onChange={handleChange}
                fullWidth
                placeholder="+ de "
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Suffixe"
                name="suffix"
                value={currentStat.suffix}
                onChange={handleChange}
                fullWidth
                placeholder=" €"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="icon-select-label">Icône</InputLabel>
                <Select
                  labelId="icon-select-label"
                  name="icon"
                  value={currentStat.icon}
                  onChange={handleChange}
                  label="Icône"
                >
                  {availableIcons.map((icon) => (
                    <MenuItem key={icon.name} value={icon.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          const IconComponent = {
                            'People': PeopleIcon,
                            'Payments': PaymentsIcon,
                            'Savings': SavingsIcon,
                            'CreditCard': CreditCardIcon,
                            'AccountBalance': AccountBalanceIcon,
                            'Apartment': ApartmentIcon,
                            'Agriculture': AgricultureIcon,
                            'EmojiEvents': EmojiEventsIcon,
                            'LocalAtm': LocalAtmIcon,
                            'MonetizationOn': MonetizationOnIcon
                          }[icon.name] || PeopleIcon;
                          
                          return <IconComponent sx={{ mr: 1 }} />;
                        })()}
                        {icon.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ordre d'affichage"
                name="order"
                type="number"
                value={currentStat.order}
                onChange={handleChange}
                fullWidth
                helperText="Ordre d'affichage des statistiques (0 = premier)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ minWidth: '100px' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{ 
              bgcolor: '#014527', 
              '&:hover': { bgcolor: '#01331d' },
              minWidth: '120px'
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Statistics;
